import React, { useEffect, useMemo, useState } from "react";
import HomeLayout from "../../asset/layout/HomeLayout";
import { getAllCampaignLeads } from "../../actions/userAuth";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Stack,
  Grid,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import CustomButton from "../../components/mui/CustomButton";
import SearchBar from "../../components/mui/SearchBar";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { format } from "date-fns";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const columns = [
  "Lead",
  "Email",
  "Phone",
  "Company",
  "Created on",
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: "100%" }}>
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.75rem",
              color: "#0f172a",
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            {value ?? "—"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const display = (val) => (val != null && val !== "" ? val : "—");

const getLeadName = (row) => {
  const parts = [row?.firstName, row?.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : row?.email || "—";
};

const getInitials = (row) => {
  const name = getLeadName(row);
  return name.charAt(0).toUpperCase();
};

const CampaignLeads = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
    }
  }, [navigate]);

  const getData = () => {
    setLoading(true);
    dispatch(
      getAllCampaignLeads(
        (data) => {
          setUserData(Array.isArray(data?.data?.data) ? data.data.data : []);
          setLoading(false);
        },
        () => setLoading(false)
      )
    );
  };

  useEffect(() => {
    getData();
  }, [dispatch]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return userData;
    const q = searchTerm.toLowerCase();
    return userData.filter((item) =>
      [
        item?.firstName,
        item?.lastName,
        item?.email,
        item?.phoneNumber,
        item?.companyName,
      ].some((field) => field?.toLowerCase?.().includes(q))
    );
  }, [userData, searchTerm]);

  const stats = useMemo(() => {
    const total = filteredData.length;
    const withCompany = filteredData.filter((l) => l?.companyName).length;
    const withEmail = filteredData.filter((l) => l?.email).length;
    const thisMonth = filteredData.filter((l) =>
      l?.createdAt ? dayjs(l.createdAt).isSame(dayjs(), "month") : false
    ).length;
    return { total, withCompany, withEmail, thisMonth };
  }, [filteredData]);

  const totalPages = Math.ceil(filteredData.length / countPerPage) || 1;
  const paginatedData = filteredData.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const formatDateIndian = (dateString) => {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy", { timeZone: "Asia/Kolkata" });
  };

  const exportToExcel = () => {
    const header = [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number",
      "Company Name",
      "Created On",
    ];
    const rows = filteredData.map((user) => [
      user?.firstName,
      user?.lastName,
      user?.email,
      user?.phoneNumber,
      user?.companyName,
      user?.updatedAt ? formatDateIndian(user.updatedAt) : "—",
    ]);
    rows.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Campaign Leads");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      "Campaign_Leads.xlsx"
    );
  };

  const premiumButtonSx = {
    height: "44px",
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(135deg, #11409B 0%, #2563eb 100%)",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #0f3580 0%, #1d4ed8 100%)",
    },
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)",
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: "24px",
              background:
                "linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(17, 64, 155, 0.35)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -30,
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: alpha("#fff", 0.06),
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <CampaignOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: "0.1em", opacity: 0.85 }}
                  >
                    MARKETING
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  Campaign leads
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  View and export leads captured from your campaigns. Search by
                  name, email, phone, or company.
                </Typography>
              </Box>
              <CustomButton
                variant="contained"
                onClick={exportToExcel}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  ...premiumButtonSx,
                  bgcolor: alpha("#fff", 0.15),
                  backdropFilter: "blur(8px)",
                  border: `1px solid ${alpha("#fff", 0.25)}`,
                  boxShadow: "none",
                  "&:hover": { bgcolor: alpha("#fff", 0.22) },
                }}
              >
                Export leads
              </CustomButton>
            </Stack>
          </Box>
        </motion.div>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={GroupsOutlinedIcon}
                label="Total leads"
                value={stats.total}
                accent="#4f46e5"
                gradient="linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={BusinessOutlinedIcon}
                label="With company"
                value={stats.withCompany}
                accent="#2563eb"
                gradient="linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={EmailOutlinedIcon}
                label="With email"
                value={stats.withEmail}
                accent="#059669"
                gradient="linear-gradient(135deg, #34d399 0%, #059669 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={CampaignOutlinedIcon}
                label="This month"
                value={stats.thisMonth}
                accent="#7c3aed"
                gradient="linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)"
              />
            </motion.div>
          </Grid>
        </Grid>

        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: "#11409B", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                Filters
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={10}>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: "2.75rem",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha("#11409B", 0.25),
                    color: "#11409B",
                    "&:hover": {
                      borderColor: "#11409B",
                      bgcolor: alpha("#11409B", 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: "#11409B" }} />
                <Typography sx={{ mt: 2, color: "#64748b" }}>
                  Loading campaign leads…
                </Typography>
              </Stack>
            </PanelCard>
          ) : paginatedData.length > 0 ? (
            <TableContainer component={PanelCard} sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha("#11409B", 0.04),
                      "& th": {
                        borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "#11409B",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          py: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row) => (
                    <TableRow
                      key={row._id || row.id || row.email}
                      sx={{
                        transition: "background 0.2s",
                        "&:hover": { bgcolor: alpha("#11409B", 0.03) },
                        "& td": {
                          borderBottom: `1px solid ${alpha("#11409B", 0.06)}`,
                          py: 1.75,
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              background:
                                "linear-gradient(135deg, #11409B, #2563eb)",
                              fontWeight: 700,
                              border: "2px solid #fff",
                              boxShadow: "0 4px 12px rgba(17,64,155,0.2)",
                            }}
                          >
                            {getInitials(row)}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                              {getLeadName(row)}
                            </Typography>
                            {(row?.firstName || row?.lastName) && row?.email && (
                              <Typography variant="caption" sx={{ color: "#64748b" }}>
                                {row.email}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#475569" }}>
                          {display(row?.email)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#475569" }}>
                          {row?.phoneNumber ? `+91 ${row.phoneNumber}` : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row?.companyName ? (
                          <Chip
                            label={row.companyName}
                            size="small"
                            sx={{
                              bgcolor: alpha("#11409B", 0.08),
                              color: "#11409B",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              maxWidth: 180,
                            }}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          {row?.updatedAt || row?.createdAt
                            ? dayjs(row.updatedAt || row.createdAt).format(
                                "DD MMM YYYY"
                              )
                            : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <CampaignOutlinedIcon
                  sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  No leads found
                </Typography>
                <Typography sx={{ color: "#64748b", mt: 1, textAlign: "center" }}>
                  Try adjusting your search or check back when new campaign leads arrive.
                </Typography>
              </Stack>
            </PanelCard>
          )}
        </motion.div>

        {!loading && filteredData.length > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, filteredData.length)} of{" "}
                  {filteredData.length}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha("#11409B", 0.2)}`,
                      borderRadius: "10px",
                      px: 1.5,
                      py: 0.75,
                      fontSize: "0.875rem",
                      color: "#0f172a",
                      bgcolor: "#fff",
                      outline: "none",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 600, minWidth: 48, textAlign: "center" }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>
    </HomeLayout>
  );
};

export default CampaignLeads;
